import { z } from "zod";

// ── Enums ───────────────────────────────────────────────────────────────
export const estatusContratoEnum = ["pendiente", "activo", "suspendido", "cancelado", "error"] as const;
export type EstatusContrato = (typeof estatusContratoEnum)[number];

export const cpeVendorEnum = ["mikrotik", "ubiquiti", "ubiquiti_ac", "cambium", "mimosa", "otro"] as const;
export type CPEVendor = (typeof cpeVendorEnum)[number];

// rc81: "ubiquiti" = serie M (airOS 6). "ubiquiti_ac" = serie AC (airOS 8).
// "otro" = genérico (sin equipo gestionable; online por sesión PPP).
export const CPE_VENDOR_LABEL: Record<CPEVendor, string> = {
  mikrotik: "MikroTik",
  ubiquiti: "Ubiquiti M",
  ubiquiti_ac: "Ubiquiti AC",
  cambium: "Cambium",
  mimosa: "Mimosa",
  otro: "Genérico",
};

export const polarizacionEnum = ["horizontal", "vertical", "dual"] as const;
export type Polarizacion = (typeof polarizacionEnum)[number];

export const onuVendorEnum = ["vsol", "tp_link", "nokia", "zte", "huawei", "otro"] as const;
export type ONUVendor = (typeof onuVendorEnum)[number];

export const ONU_VENDOR_LABEL: Record<ONUVendor, string> = {
  vsol: "VSOL",
  tp_link: "TP-Link",
  nokia: "Nokia",
  zte: "ZTE",
  huawei: "Huawei",
  otro: "Otro",
};

export const splitterNivelEnum = ["1x4", "1x8", "1x16", "1x32", "1x64"] as const;
export type SplitterNivel = (typeof splitterNivelEnum)[number];

// F92 — Equipo en comodato vs propiedad
export const equipoModalidadEnum = ["comodato", "propiedad_cliente"] as const;
export type EquipoModalidad = (typeof equipoModalidadEnum)[number];

export const EQUIPO_MODALIDAD_LABEL: Record<EquipoModalidad, string> = {
  comodato: "Comodato (ISP recupera)",
  propiedad_cliente: "Propiedad del cliente",
};

const ipRegex = /^\d{1,3}(\.\d{1,3}){3}$/;

// ── Wireless detalle ────────────────────────────────────────────────────
// Los enums opcionales TIENEN que aceptar "" porque los <Select> devuelven
// string vacío cuando el usuario elige "—" (default). Sin esto Zod rechaza
// silenciosamente y el form no se somete.
export const wirelessDetalleSchema = z.object({
  cpe_vendor: z.enum(cpeVendorEnum).optional().or(z.literal("")),
  cpe_modelo: z.string().max(100).optional().or(z.literal("")),
  cpe_mac: z.string().max(20).optional().or(z.literal("")),
  cpe_serial: z.string().max(100).optional().or(z.literal("")),
  cpe_ip_management: z.string().regex(ipRegex, "IP inválida").optional().or(z.literal("")),
  cpe_usuario_admin: z.string().max(50).optional().or(z.literal("")),
  cpe_password_admin: z.string().optional().or(z.literal("")),
  ssid_conexion: z.string().max(100).optional().or(z.literal("")),
  frecuencia_mhz: z.union([z.coerce.number().int().positive().max(200_000), z.literal("")]).optional(),
  polarizacion: z.enum(polarizacionEnum).optional().or(z.literal("")),
  sitio_id: z.string().uuid().optional().or(z.literal("")),
  notas_instalacion: z.string().optional().or(z.literal("")),
});

// ── Fiber detalle ───────────────────────────────────────────────────────
export const fiberDetalleSchema = z.object({
  onu_vendor: z.enum(onuVendorEnum).optional().or(z.literal("")),
  onu_modelo: z.string().max(100).optional().or(z.literal("")),
  onu_serial: z.string().max(50).optional().or(z.literal("")),
  onu_mac: z.string().max(20).optional().or(z.literal("")),
  onu_ip_management: z.string().regex(ipRegex, "IP inválida").optional().or(z.literal("")),
  onu_usuario_admin: z.string().max(50).optional().or(z.literal("")),
  onu_password_admin: z.string().optional().or(z.literal("")),
  olt_puerto: z.string().max(50).optional().or(z.literal("")),
  splitter_nivel: z.enum(splitterNivelEnum).optional().or(z.literal("")),
  potencia_optica_dbm_esperada: z.union([z.coerce.number(), z.literal("")]).optional(),
  distancia_m_estimada: z.union([z.coerce.number().int().min(0).max(200_000), z.literal("")]).optional(),
  sitio_olt_id: z.string().uuid().optional().or(z.literal("")),
  notas_instalacion: z.string().optional().or(z.literal("")),
});

// ── ContratoCreate ──────────────────────────────────────────────────────
export const contratoCreateSchema = z.object({
  cliente_id: z.string().uuid("Cliente requerido"),
  plan_id: z.string().uuid("Plan requerido"),
  router_id: z.string().uuid("Router requerido"),
  red_id: z.string().uuid("Red requerida"),
  ip_asignada: z.string().regex(ipRegex, "IP inválida").optional().or(z.literal("")),
  pppoe_user: z.string().max(50).optional().or(z.literal("")),
  pppoe_password: z.string().max(100).optional().or(z.literal("")),
  fecha_corte: z.coerce.number().int().min(1, "Día 1–28").max(28, "Día 1–28"),
  // Orden del union importa: probamos z.literal("") PRIMERO para que un
  // campo vacío quede como "" (luego clean() lo quita del payload).
  // z.coerce.number() convertiría Number("") === 0 si estuviera primero,
  // resultando en precio_override=0 en DB → facturas con total $0.
  // Bug histórico en dev <= 57. También ahora .positive() excluye 0 explícito.
  precio_override: z.union([
    z.literal(""),
    z.coerce.number().positive({ message: "Debe ser > 0, o déjalo vacío" }),
  ]).optional(),
  descuento_pct: z.coerce.number().min(0).max(100).default(0),
  dias_gracia_override: z.union([
    z.literal(""),
    z.coerce.number().int().min(0).max(60),
  ]).optional(),
  exento_facturacion: z.boolean().default(false),
  exento_monitoreo: z.boolean().default(false),
  // F92 — Equipo
  equipo_modalidad: z.enum(equipoModalidadEnum).default("comodato"),
  equipo_descripcion: z.string().max(300).optional().or(z.literal("")),
  equipo_costo_instalacion: z.coerce.number().min(0).default(0),
  // rc124 — monto confirmado en el modal de factura de instalación (0/ausente = sin factura)
  factura_instalacion_monto: z.coerce.number().min(0).optional(),
  // rc145 — ubicación GPS de la instalación desde el alta (mismo contrato
  // de valores que en update: "" = sin capturar, clean() lo quita)
  latitud: z.union([z.literal(""), z.coerce.number().min(-90, "Entre -90 y 90").max(90, "Entre -90 y 90")]).optional(),
  longitud: z.union([z.literal(""), z.coerce.number().min(-180, "Entre -180 y 180").max(180, "Entre -180 y 180")]).optional(),
  ubicacion_referencia: z.string().max(200).optional().or(z.literal("")),
  notas: z.string().optional().or(z.literal("")),
  notas_internas: z.string().optional().or(z.literal("")),
  tags: z.array(z.string()).default([]),
  wireless_detalle: wirelessDetalleSchema.optional(),
  fiber_detalle: fiberDetalleSchema.optional(),
  // rc108 — alta de fibra vía ByteOLT: ONU elegida + plantilla/perfil.
  // Obligatorio server-side cuando el plan es de fibra (integración activa).
  byteolt: z
    .object({
      serial: z.string().min(4).max(64),
      template_id: z.string().optional().or(z.literal("")),
      line_profile: z.string().optional().or(z.literal("")),
      // rc110 — elegida del grupo "ya aprovisionadas sin cliente": solo se liga
      ya_aprovisionada: z.boolean().optional(),
      // rc112 — pre-registro: la OLT aún no ve la ONU (requiere olt_id)
      pre_registro: z.boolean().optional(),
      olt_id: z.string().optional().or(z.literal("")),
      // rc118 — MATCH: ya aprovisionada Y ligada (cliente importado) — solo
      // se re-liga en ByteOLT, cero órdenes a la OLT y NADA a ByteACS.
      solo_match: z.boolean().optional(),
    })
    .optional(),
  // rc111 — serial del módem para ByteACS (default: la serie de la ONU)
  // rc121 — + clave WiFi opcional (8-63) asignada desde el alta
  byteacs: z.object({
    serial: z.string().min(4).max(64),
    wifi_pass: z.string().min(8).max(63).optional(),
    // rc122 — SSID personalizado (conservar la red anterior del cliente)
    ssid: z.string().min(1).max(32).optional(),
  }).optional(),
});

/** Campos editables del form (sin motivo). El motivo se pide en modal aparte. */
export const contratoUpdateFieldsSchema = z.object({
  // Plan (v1.5.0-rc16+) — solo admin/superadmin puede cambiarlo. Al cambiar
  // se resetea precio_override a NULL en backend automáticamente. Facturas
  // ya emitidas NO se tocan (son inmutables).
  plan_id: z.string().uuid().optional().or(z.literal("")),
  // Básicos
  fecha_corte: z.coerce.number().int().min(1).max(28).optional(),
  // Orden del union importa: probamos z.literal("") PRIMERO para que un
  // campo vacío quede como "" (luego clean() lo quita del payload).
  // z.coerce.number() convertiría Number("") === 0 si estuviera primero,
  // resultando en precio_override=0 en DB → facturas con total $0.
  // Bug histórico en dev <= 57. También ahora .positive() excluye 0 explícito.
  precio_override: z.union([
    z.literal(""),
    z.coerce.number().positive({ message: "Debe ser > 0, o déjalo vacío" }),
  ]).optional(),
  descuento_pct: z.coerce.number().min(0).max(100).optional(),
  dias_gracia_override: z.union([
    z.literal(""),
    z.coerce.number().int().min(0).max(60),
  ]).optional(),
  exento_facturacion: z.boolean().optional(),
  exento_monitoreo: z.boolean().optional(),
  // rc90 — ¿poller sigue facturando estando suspendido? (default false)
  facturar_suspendido: z.boolean().optional(),
  // rc97 — excluir del reintento masivo de errores
  excluir_reintento_auto: z.boolean().optional(),
  // rc101 — ubicación GPS de la instalación (por contrato)
  latitud: z.union([z.literal(""), z.coerce.number().min(-90).max(90)]).optional(),
  longitud: z.union([z.literal(""), z.coerce.number().min(-180).max(180)]).optional(),
  ubicacion_referencia: z.string().max(200).optional().or(z.literal("")),
  // rc44 — tolerancia facturas vencidas (0-12)
  max_facturas_vencidas_permitidas: z.coerce.number().int().min(0).max(12).optional(),
  // F92 — Equipo
  equipo_modalidad: z.enum(equipoModalidadEnum).optional(),
  equipo_descripcion: z.string().max(300).optional().or(z.literal("")),
  equipo_costo_instalacion: z.coerce.number().min(0).optional(),
  notas: z.string().optional().or(z.literal("")),
  notas_internas: z.string().optional().or(z.literal("")),
  tags: z.array(z.string()).optional(),
  // PPPoE editables (afectan router)
  pppoe_user: z.string().max(50).optional().or(z.literal("")),
  pppoe_password: z.string().max(100).optional().or(z.literal("")),
  // Detalles equipo (afectan router si cambia MAC)
  wireless_detalle: wirelessDetalleSchema.optional(),
  fiber_detalle: fiberDetalleSchema.optional(),
});

/** Schema completo para enviar al API (con motivo obligatorio). */
export const contratoUpdateSchema = contratoUpdateFieldsSchema.extend({
  motivo: z.string().min(5, "Motivo mínimo 5 caracteres").max(500),
});

export type ContratoCreateInput = z.infer<typeof contratoCreateSchema>;
export type ContratoUpdateInput = z.infer<typeof contratoUpdateSchema>;
export type ContratoUpdateFieldsInput = z.infer<typeof contratoUpdateFieldsSchema>;
export type WirelessDetalleInput = z.infer<typeof wirelessDetalleSchema>;
export type FiberDetalleInput = z.infer<typeof fiberDetalleSchema>;

// ── Response types ──────────────────────────────────────────────────────
export type ContratoSummary = {
  id: string;
  codigo: string;
  cliente_id: string;
  // dev116 — nombre legible del cliente para listas
  cliente_nombre?: string | null;
  // rc20+ — empresa del cliente para chip multi-tenant
  cliente_empresa_id?: number | null;
  plan_id: string;
  router_id: string;
  red_id: string;
  ip_asignada: string;
  pppoe_user: string;
  fecha_corte: number;
  estatus: EstatusContrato;
  fecha_activacion: string | null;
  created_at: string;
  // F92
  equipo_modalidad: EquipoModalidad;
  // rc97 — excluido del reintento masivo de errores
  excluir_reintento_auto?: boolean;
};

export type WirelessDetalleOut = {
  cpe_vendor: CPEVendor | null;
  cpe_modelo: string | null;
  cpe_mac: string | null;
  cpe_mac_historica: string | null;
  cpe_serial: string | null;
  cpe_ip_management: string | null;
  cpe_usuario_admin: string | null;
  ssid_conexion: string | null;
  frecuencia_mhz: number | null;
  polarizacion: Polarizacion | null;
  sitio_id: string | null;
  notas_instalacion: string | null;
};

export type FiberDetalleOut = {
  onu_vendor: ONUVendor | null;
  onu_modelo: string | null;
  onu_serial: string | null;
  onu_mac: string | null;
  onu_mac_historica: string | null;
  onu_ip_management: string | null;
  onu_usuario_admin: string | null;
  olt_puerto: string | null;
  splitter_nivel: SplitterNivel | null;
  potencia_optica_dbm_esperada: string | number | null;
  distancia_m_estimada: number | null;
  sitio_olt_id: string | null;
  notas_instalacion: string | null;
};

export type ContratoDetail = ContratoSummary & {
  precio_override: string | null;
  descuento_pct: string;
  dias_gracia_override: number | null;
  exento_facturacion: boolean;
  exento_monitoreo: boolean;
  fecha_suspension: string | null;
  fecha_cancelacion: string | null;
  motivo_cancelacion: string | null;
  ultimo_error_router: string | null;
  ultimo_intento_router: string | null;
  fallos_consecutivos: number;
  // F92 — Equipo
  equipo_modalidad: EquipoModalidad;
  // rc44 — tolerancia facturas vencidas (default 0, max 12)
  max_facturas_vencidas_permitidas?: number;
  // rc90 — facturación automática estando suspendido (default false)
  facturar_suspendido?: boolean;
  // rc97 — excluido del reintento masivo de errores (default false)
  excluir_reintento_auto?: boolean;
  // rc101 — ubicación GPS de la instalación (por contrato)
  latitud?: string | number | null;
  longitud?: string | number | null;
  ubicacion_referencia?: string | null;
  // rc90 — reinicio de ciclo: el poller no emite ciclos antes de esta fecha
  // (la setea reanclar_ciclo; solo lectura)
  facturar_desde?: string | null;
  // rc90 — revisión de ciclo pendiente (pago tardío sin decisión humana)
  ciclo_revision_fecha?: string | null;
  equipo_descripcion: string | null;
  equipo_costo_instalacion: string;
  equipo_recuperado_at: string | null;
  equipo_recuperado_por: string | null;
  notas: string | null;
  notas_internas: string | null;
  tags: string[];
  wireless_detalle: WirelessDetalleOut | null;
  fiber_detalle: FiberDetalleOut | null;
  // F18.1 — Firma del contrato
  contrato_firmado_at: string | null;
  contrato_firmado_url: string | null;
  contrato_firmado_by: string | null;
  contrato_firma_metodo: "manual" | "electronico" | null;
};

export type ContratoCreateResponse = ContratoDetail & {
  pppoe_password_plain: string;
};

export type PagedContratos = {
  items: ContratoSummary[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type RegenerarPasswordResponse = {
  pppoe_user: string;
  pppoe_password_plain: string;
  mensaje: string;
};
